/**
 * HeaderContainer - Componente principal do Header
 * 
 * Comportamento por página:
 * HOME sem scroll: Logo branco + texto escuro + fundo transparente
 * HOME com scroll: Logo branco + texto branco + fundo bg-scroll.jpg
 * PÁGINAS INTERNAS sem scroll: Logo branco + texto branco + fundo transparente
 * PÁGINAS INTERNAS com scroll: Logo branco + texto branco + fundo bg-scroll.jpg
 * 
 * Melhorias implementadas:
 * ✅ Sem duplicação de logos
 * ✅ Cores centralizadas em constantes
 * ✅ Sub-componentes para Menu Items
 * ✅ Design exato conforme prints
 * ✅ Imagem de fundo no scroll
 */

'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { MobileMenuSheet } from './MobileMenuSheet';
import { MenuItem } from './MenuItem';
import { IMenuData } from '@/services/header/type';
import { ScrollArea } from '../../atoms/scroll-area';
import { useNavigation } from '@/context/NavigationContext';
import { useNotification } from './useNotification';
import Image from 'next/legacy/image';
import { SearchGlobalBtn } from './SearchGlobalBtn';
import LanguageSelector from '@/components/atoms/language-selector';
import { ISiteInfo } from '@/services/types';
import { HEADER_COLORS } from './headerColors';

interface IHeaderContainerProps extends IMenuData, ISiteInfo { }

// Constantes
const MENU_CLOSE_DELAY = 150;
const NOTIFICATION_DURATION = 3000;
const NOTIFICATION_MESSAGE = '⚠️ Serviço brevemente disponível.';
const SCROLL_THRESHOLD = 50;

export function HeaderContainer({
  menuGroups,
  logo,
  logo_white,
}: IHeaderContainerProps) {
  const { headerTheme } = useNavigation();
  const { isVisible, show } = useNotification(
    NOTIFICATION_MESSAGE,
    NOTIFICATION_DURATION
  );

  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState<number | null>(null);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handlers
  const handleMouseEnter = (idx: number) => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    if (menuGroups?.[idx]?.subMenu && menuGroups[idx].subMenu.length > 0) {
      setOpenMenu(idx);
    }
  };

  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => setOpenMenu(null), MENU_CLOSE_DELAY);
  };

  const handleUnavailableClick = (e: React.MouseEvent) => {
    e.preventDefault();
    show();
  };

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > SCROLL_THRESHOLD);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  // Lógica de tema: Home (light) vs Páginas Internas (dark)
  // Se headerTheme for 'light', é home. Caso contrário, é página interna.
  const isHomePage = headerTheme === 'light';
  const isInternalPage = !isHomePage;

  // Logo branca:
  // - Páginas internas sem scroll e sem menu aberto: logo branca
  // - Com scroll ou menu aberto: logo branca (sobre bg-scroll.jpg)
  // - Home sem scroll: logo colorida (normal)
  const shouldShowWhiteLogo = isInternalPage || scrolled || openMenu !== null;

  const logoSrc = shouldShowWhiteLogo ? logo_white?.url : logo?.url;
  const logoAlt = shouldShowWhiteLogo ? logo_white?.alternativeText : logo?.alternativeText;

  // Background: transparente sem scroll, bg-scroll.jpg com scroll ou menu aberto
  const hasBackground = scrolled || openMenu !== null;
  const headerBg = hasBackground
    ? 'transparent' // Será aplicado via backgroundImage
    : HEADER_COLORS.background.transparent;

  // Cor texto:
  // - Páginas internas sem scroll: branco
  // - Com scroll ou menu aberto: branco (sobre bg-scroll.jpg)
  // - Home sem scroll: escuro
  const shouldUseWhiteText = isInternalPage || scrolled || openMenu !== null;
  const textColor = shouldUseWhiteText
    ? HEADER_COLORS.text.light
    : HEADER_COLORS.text.dark;

  return (
    <>
      <motion.header
        initial={{
          height: 98,
          backgroundColor: HEADER_COLORS.background.transparent,
        }}
        animate={{
          height: scrolled ? 68 : 98,
          backgroundColor: headerBg,
        }}
        transition={{
          type: 'spring',
          damping: 30,
          stiffness: 200,
        }}
        className={`fixed top-0 left-0 w-full z-60`}
        style={{
          color: textColor,
          backgroundImage: hasBackground ? 'url(/bg-scroll.jpg)' : 'none',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <div className="relative w-full h-full">
          {/* Notificação */}
          {isVisible && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="fixed top-4 right-4 bg-yellow-100 text-orange-600 px-4 py-2 rounded shadow z-50"
            >
              {NOTIFICATION_MESSAGE}
            </motion.div>
          )}

          <div className="container mx-auto flex items-center justify-between gap-3 lg:gap-6 h-full px-4 sm:px-6">
            {/* Logo */}
            {logoSrc && (
              <Link href="/" className="flex-shrink-0">
                <Image
                  width={160}
                  height={64}
                  src={logoSrc}
                  alt={logoAlt || 'logo'}
                  className="w-[110px] sm:w-[140px] md:w-[160px] h-auto object-contain"
                  priority
                />
              </Link>
            )}

            {/* Menu Desktop */}
            <nav className="hidden xl:flex flex-1 justify-center">
              <ul className="flex items-center space-x-8 font-normal">
                {menuGroups?.map((group, idx) => {
                  const isHovered = openMenu === idx;

                  return (
                    <MenuItem
                      key={`menu-${idx}-${group.label}`}
                      group={group}
                      isHovered={isHovered}
                      isDarkText={!shouldUseWhiteText}
                      onMouseEnter={() => handleMouseEnter(idx)}
                      onMouseLeave={handleMouseLeave}
                    />
                  );
                })}
              </ul>
            </nav>

            {/* Ações: Busca, Portal Kre+, Idioma, Menu Mobile */}
            <div className="flex items-center space-x-2 sm:space-x-3">
              <SearchGlobalBtn isDarkTheme={!shouldUseWhiteText} />

              {/* Botão Portal Kre+ - Aparece sempre em páginas internas, espaço reservado em home */}
              <div className="hidden sm:flex">
                {isInternalPage ? (
                  // Páginas internas: botão visível (sempre, com ou sem scroll)
                  <Link
                    href={process.env.NEXT_PUBLIC_PORTAL_KREMAIS_URL || 'https://kremais-sta.gov.cv/'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-2 rounded-full text-sm font-semibold transition-all"
                    style={{
                      backgroundColor: HEADER_COLORS.primary.teal,
                      color: 'white',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.opacity = '0.9';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.opacity = '1';
                    }}
                  >
                    Portal Kre+
                  </Link>
                ) : (
                  // Home: espaço reservado vazio (mantém layout)
                  <div className="px-4 py-2 w-28" />
                )}
              </div>

              <LanguageSelector isScrolled={scrolled} className="" />
              <MobileMenuSheet
                menuGroups={menuGroups}
                isWhiteTheme={shouldUseWhiteText}
                logoSrc={logoSrc}
                logoAlt={logoAlt}
              />
            </div>
          </div>

          {/* Menu Expandido (Desktop) */}
          <AnimatePresence mode="wait">
            {openMenu !== null &&
              menuGroups[openMenu]?.subMenu &&
              menuGroups[openMenu].subMenu.length > 0 && (
                <motion.div
                  onMouseEnter={() => {
                    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
                  }}
                  onMouseLeave={handleMouseLeave}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{
                    type: 'spring',
                    damping: 25,
                    stiffness: 200,
                  }}
                  className="absolute top-full left-0 w-full z-50"
                  style={{
                    backgroundColor: '#fff',
                    borderTop: `0px solid ${HEADER_COLORS.primary.orange}`,
                  }}
                >
                  <ScrollArea className="min-h-[400px]">
                    <div className="container mx-auto px-8 py-12">
                      <div className="grid grid-cols-3 gap-16">


                        {/* Colunas do Menu */}
                        {menuGroups[openMenu].subMenu.map((menu, mIdx) => (
                          <div
                            key={`menu-col-${openMenu}-${mIdx}`}
                            className="space-y-6"
                          >
                            <h3
                              className="text-sm font-bold uppercase tracking-widest"
                              style={{ color: HEADER_COLORS.primary.teal }}
                            >
                              {menu.title}
                            </h3>
                            <ul className="space-y-2">
                              {menu?.subMenu?.map((sub, sIdx) => (
                                <li
                                  key={`sub-${openMenu}-${mIdx}-${sIdx}`}
                                  onClick={() => setOpenMenu(null)}
                                >
                                  {sub.url ? (
                                    sub.external_link ? (
                                      <a
                                        href={sub.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-sm font-normal transition-colors hover:font-semibold"
                                        style={{
                                          color: HEADER_COLORS.text.muted,
                                        }}
                                        onMouseEnter={(e) => {
                                          e.currentTarget.style.color =
                                            HEADER_COLORS.primary.yellow;
                                        }}
                                        onMouseLeave={(e) => {
                                          e.currentTarget.style.color =
                                            HEADER_COLORS.text.muted;
                                        }}
                                      >
                                        {sub.label}
                                      </a>
                                    ) : (
                                      <Link href={sub.url}>
                                        <span
                                          className="text-sm font-normal transition-colors hover:font-semibold cursor-pointer"
                                          style={{
                                            color: HEADER_COLORS.text.muted,
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.color =
                                              HEADER_COLORS.primary.yellow;
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.color =
                                              HEADER_COLORS.text.muted;
                                          }}
                                        >
                                          {sub.label}
                                        </span>
                                      </Link>
                                    )
                                  ) : (
                                    <span
                                      className="text-sm font-normal cursor-pointer transition-colors hover:font-semibold"
                                      style={{
                                        color: HEADER_COLORS.text.muted,
                                      }}
                                      onClick={handleUnavailableClick}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.color =
                                          HEADER_COLORS.primary.yellow;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.color =
                                          HEADER_COLORS.text.muted;
                                      }}
                                    >
                                      {sub.label}
                                    </span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </ScrollArea>
                </motion.div>
              )}
          </AnimatePresence>
        </div>
      </motion.header>
    </>
  );
}